import {
  IonButton,
  IonContent,
  IonInput,
  IonLoading,
  IonModal,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import React, { useRef } from 'react';

import {
  ButtonContainer,
  ModalHeader,
  Padding,
  Spacer
} from '@/components/common';
import { Icon, Text } from '@/components/memo';
import { RescheduleProvider, useReschedule } from '@/contexts';
import { Request } from '@/data/Requests';
import { useKeyboardAwareInput } from '@/hooks';
import { now } from '@/utils/helpers/now';
import { parseTime } from '@/utils/helpers/parseTime';
import { addMonths, endOfYear, format, isAfter, isEqual } from 'date-fns';
import { arrowBackOutline, warning } from 'ionicons/icons';

import './Reschedule.css';

interface RescheduleProps {
  isOpen: boolean;
  onClose: () => void;
  requestToReschedule?: Request | null;
  setSelectedDate?: React.Dispatch<React.SetStateAction<Date>>;
  presentingElement?: HTMLElement | undefined;
}

const Reschedule: React.FC<RescheduleProps> = ({
  isOpen,
  onClose,
  presentingElement
}) => {
  const {
    date,
    startTime,
    endTime,
    reason,
    loading,
    errors,
    onInput,
    submit,
    reset,
    timeOptions,
    conflictingRequest,
    inputRef,
    onKeyDown
  } = useReschedule();

  const modal = useRef<HTMLIonModalElement | null>(null);
  const containerRef = useRef<HTMLIonContentElement | null>(null);
  const reasonInputRef = useRef<HTMLIonInputElement | null>(null);

  /**
   * Manages keyboard-aware scrolling behavior for the reason input field in the modal.
   * This custom implementation addresses the limitations of the default hook behavior
   * in this specific context where the modal's bottom space is constrained.
   *
   * @see useKeyboardAwareInput - The base hook that provides keyboard awareness functionality
   * @note This implementation ensures the input field remains visible when the keyboard appears,
   *       preventing it from being obscured by the keyboard in the modal's constrained space.
   */
  useKeyboardAwareInput({
    inputRef: reasonInputRef,
    containerRef
  });

  const copy = {
    subHeader: 'RESCHEDULE',
    header: (
      <>
        Reschedule <Text color="primary">appointment</Text>
      </>
    ),
    message: 'Please select a new date and time for the appointment.',
    datePlaceholder: 'Select a date',
    startTimePlaceholder: 'Select a start time',
    endTimePlaceholder: 'Select an end time',
    reasonPlaceholder: 'Enter reason for rescheduling'
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => {
        reset();
        onClose();
      }}
      presentingElement={presentingElement}
      ref={modal}
    >
      <IonContent ref={containerRef}>
        <Padding withSafeArea={!presentingElement}>
          <div className="reschedule">
            <ModalHeader
              leftIcon={arrowBackOutline}
              onClickLeftIcon={() => {
                reset();
                modal.current?.dismiss();
              }}
            />
            <Spacer top={16} bottom={24}>
              <h4 className="reschedule__sub-header">{copy.subHeader}</h4>
              <h1 className="reschedule__header">{copy.header}</h1>
              <p className="reschedule__message">{copy.message}</p>
            </Spacer>

            <Spacer bottom={124}>
              <div className="reschedule__form">
                <div
                  className={`reschedule__input ${errors.date ? 'reschedule__input--error' : ''}`}
                >
                  <IonInput
                    ref={inputRef}
                    label="Date"
                    labelPlacement="stacked"
                    value={date}
                    type="date"
                    placeholder={copy.datePlaceholder}
                    onIonInput={(e: CustomEvent) =>
                      onInput('date', e.detail.value!)
                    }
                    onKeyDown={onKeyDown}
                    min={format(now(), 'yyyy-MM-dd')}
                    max={
                      isAfter(endOfYear(now()), addMonths(now(), 3))
                        ? format(endOfYear(now()), 'yyyy-MM-dd')
                        : format(endOfYear(addMonths(now(), 3)), 'yyyy-MM-dd')
                    }
                  />
                </div>
                <div
                  className={`reschedule__input ${errors.startTime ? 'reschedule__input--error' : ''}`}
                >
                  <IonSelect
                    label="Start Time"
                    labelPlacement="stacked"
                    value={startTime}
                    compareWith={(a: string, b: string) =>
                      isEqual(parseTime(a), parseTime(b))
                    }
                    placeholder={copy.startTimePlaceholder}
                    onIonChange={(e: CustomEvent) =>
                      onInput('startTime', e.detail.value!)
                    }
                  >
                    {timeOptions.slice(0, -1).map((time) => (
                      <IonSelectOption key={time} value={time}>
                        {format(parseTime(time), 'h:mm a')}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </div>
                <div
                  className={`reschedule__input ${errors.endTime ? 'reschedule__input--error' : ''}`}
                >
                  <IonSelect
                    label="End Time"
                    labelPlacement="stacked"
                    value={endTime}
                    compareWith={(a: string, b: string) =>
                      isEqual(parseTime(a), parseTime(b))
                    }
                    placeholder={copy.endTimePlaceholder}
                    onIonChange={(e: CustomEvent) =>
                      onInput('endTime', e.detail.value!)
                    }
                  >
                    {timeOptions
                      .filter(
                        (time) =>
                          !startTime ||
                          isAfter(parseTime(time), parseTime(startTime))
                      )
                      .map((time) => (
                        <IonSelectOption key={time} value={time}>
                          {format(parseTime(time), 'h:mm a')}
                        </IonSelectOption>
                      ))}
                  </IonSelect>
                </div>
                <div className="reschedule__input">
                  <IonInput
                    ref={reasonInputRef}
                    label="Reason (optional)"
                    labelPlacement="stacked"
                    value={reason}
                    type="text"
                    placeholder={copy.reasonPlaceholder}
                    onIonInput={(e: CustomEvent) =>
                      onInput('reason', e.detail.value!)
                    }
                    onKeyDown={onKeyDown}
                  />
                </div>
                {conflictingRequest && (
                  <div className="reschedule__conflict-warning">
                    <Icon icon={warning} />
                    <Text color="warning">
                      <strong>Caution:</strong> The selected timeslot overlaps
                      with an existing appointment. You may continue, but ensure
                      this is intentional.
                    </Text>
                  </div>
                )}
              </div>
            </Spacer>

            <ButtonContainer>
              <IonButton
                id="reschedule-button"
                onClick={submit}
                expand="block"
                fill="solid"
                size="default"
                color="primary"
                strong={true}
              >
                Reschedule Appointment
              </IonButton>
            </ButtonContainer>
          </div>
        </Padding>
        <IonLoading isOpen={loading} message="Loading..." spinner="crescent" />
      </IonContent>
    </IonModal>
  );
};

const RescheduleWithProvider: React.FC<RescheduleProps> = (props) => {
  return (
    <RescheduleProvider {...props}>
      <Reschedule {...props} />
    </RescheduleProvider>
  );
};

export default RescheduleWithProvider;
